import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { PersonaVende, PersonaVendeResumen } from './persona-vende';

export interface PersonalVendeState {
  list: PaginatedList<PersonaVendeResumen>;
  current: PersonaVende;
  actives: PersonaVendeResumen[];

  nuevo: () => void;
  cargarPaginadas: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivas: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarPersonaVende) => Promise<number | null>;
  actualizar: (id: number, request: ActualizarPersonaVende) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarPersonaVende {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

export interface ActualizarPersonaVende {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

const { showSuccessToast } = useUIStore.getState();

export const usePersonalVendeStore = create<PersonalVendeState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as PersonaVende,
  actives: [],

  nuevo: () =>
    set({
      current: {} as PersonaVende
    }),
  cargarPaginadas: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<PersonaVendeResumen>>(
      `/personal-vende/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivas: async () => {
    const data = await makeGet<Array<PersonaVendeResumen>>('/personal-vende/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<PersonaVende>(`/personal-vende/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarPersonaVende) => {
    const data = await makePost<RegistrarPersonaVende, number | null>(
      '/personal-vende/',
      request
    );

    if (data.isFailure) return null;

    showSuccessToast('Registro agregado');

    set({
      current: {} as PersonaVende
    });

    return data.result;
  },
  actualizar: async (id: number, request: ActualizarPersonaVende) => {
    const data = await makePatch<ActualizarPersonaVende, PersonaVende>(
      `/personal-vende/${id}`,
      request
    );

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/personal-vende/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activa: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/personal-vende/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  }
}));
